import { Button, Typography } from "@suraasa/placebo-ui"
import api from "api"
import { QualificationType } from "api/resources/lead/types"
import { APIError } from "api/utils"
import GoogleLocationSelect, {
  AddressTypeEnum,
} from "components/GoogleLocationSelect"
import { useState } from "react"
import { toast } from "react-toastify"

import AutoSaveDateField from "./AutoSaveDateField"
import AutoSaveSelect, { formatSelectValue } from "./AutoSaveSelect"
import { useUserLeadContext } from "./Context"
import QualificationFormDialog from "./QualificationDialog"
import QualificationRow from "./QualificationRow"
import StepBar from "./StepBar"
import {
  curriculaChoices,
  genderChoices,
  maritalStatusChoices,
  yearsOfExperienceChoices,
} from "./utils/types"

const Step1 = () => {
  const { leadProfile, refreshData } = useUserLeadContext()
  const {
    gender,
    dateOfBirth,
    yearsOfExperience,
    curriculumsTaught,
    maritalStatus,
    qualifications,
    country,
    state,
    id,
  } = leadProfile || {}

  const [showAddQualificationDialog, setShowAddQualificationDialog] =
    useState<QualificationType | null>(null)

  const subjectsQualifications =
    qualifications?.filter(
      q => q.qualificationType === QualificationType.SUBJECT_QUALIFICATION
    ) || []
  const teachingQualifications =
    qualifications?.filter(
      q => q.qualificationType === QualificationType.TEACHING_QUALIFICATION
    ) || []

  const unCategorisedQualifications =
    qualifications?.filter(q => !q.qualificationType) || []

  const save = async (data: any) => {
    try {
      await api.lead.saveData({
        data,
        params: { userPrimaryKey: id },
      })
      return true
    } catch (error) {
      if (error instanceof APIError) {
        const err = error.errors

        if (err.message) {
          toast.error(err.message)
          return
        }
        if (err.fieldErrors) {
          // mapErrors(setError, err, [[saveKey]])
          return
        }
      }
    }

    return undefined
  }

  return (
    <>
      {showAddQualificationDialog && (
        <QualificationFormDialog
          onUpdate={refreshData}
          open={!!showAddQualificationDialog}
          handleClose={() => setShowAddQualificationDialog(null)}
          defaultQualificationType={showAddQualificationDialog}
          suraasaLeadId={id}
        />
      )}
      <StepBar number={1} text="Get to know your profile" className="my-2" />
      <div className="flex flex-col gap-2">
        <div className="rounded-2xl border border-onSurface-200 bg-white p-3">
          <Typography variant="title2">Personal Details</Typography>
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              {/* <AutoSaveTextField
                label="Country"
                placeholder="Ex - India"
                saveKey="country"
                userPrimaryKey={id || ""}
                value={country || ""}
              />
              <AutoSaveTextField
                label="State"
                placeholder="Ex - Maharashtra"
                saveKey="state"
                userPrimaryKey={id || ""}
                value={state || ""}
              /> */}

              <GoogleLocationSelect
                value={{ country, state }}
                fullWidth
                label="City/District"
                required
                className="w-full"
                placeHolder="Search here..."
                onChange={value => {
                  const country = value.find(item =>
                    item.types.includes(AddressTypeEnum.COUNTRY)
                  )
                  const state = value.find(item =>
                    item.types.includes(AddressTypeEnum.STATE)
                  )

                  save({
                    country: country
                      ? { name: country.longText, isoCode: country.shortText }
                      : null,
                    state: state
                      ? { name: state.longText, isoCode: state.shortText }
                      : null,
                  })
                }}
              />

              {/* <AutoSaveTextField
                label="City"
                placeholder="Ex - Mumbai"
                saveKey="city"
                userPrimaryKey={id || ""}
                value={city || ""}
              /> */}
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <AutoSaveDateField
                label="Date of Birth"
                saveKey="dateOfBirth"
                userPrimaryKey={id || ""}
                value={dateOfBirth || ""}
              />
              <AutoSaveSelect
                label="Gender"
                placeholder="Ex - Female"
                saveKey="gender"
                value={formatSelectValue(gender, genderChoices)}
                userPrimaryKey={id || ""}
                options={genderChoices}
              />

              <AutoSaveSelect
                label="Marital Status"
                placeholder="Ex - Unmarried"
                saveKey="maritalStatus"
                value={formatSelectValue(maritalStatus, maritalStatusChoices)}
                options={maritalStatusChoices}
                userPrimaryKey={id || ""}
              />
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-onSurface-200 bg-white p-3">
          <Typography variant="title2" className="mb-3">
            Education Details
          </Typography>

          <div className="mb-3 divide-y">
            {unCategorisedQualifications?.map(qualification => (
              <QualificationRow
                data={qualification}
                key={qualification.id}
                onUpdate={refreshData}
                onDelete={refreshData}
              />
            ))}
          </div>

          <div className="mb-3">
            <Typography variant="title3" className="mb-1">
              Subject Qualifications
            </Typography>
            <div className="divide-y">
              {subjectsQualifications?.map(qualification => (
                <QualificationRow
                  data={qualification}
                  key={qualification.id}
                  onUpdate={refreshData}
                  onDelete={refreshData}
                />
              ))}
            </div>
            <Button
              variant="outlined"
              onClick={() => {
                setShowAddQualificationDialog(
                  QualificationType.SUBJECT_QUALIFICATION
                )
              }}
              size="sm"
            >
              Add Subject Qualification
            </Button>
          </div>

          <div>
            <Typography variant="title3" className="mb-1">
              Teaching Qualifications
            </Typography>
            <div className="divide-y">
              {teachingQualifications?.map(qualification => (
                <QualificationRow
                  data={qualification}
                  key={qualification.id}
                  onUpdate={refreshData}
                  onDelete={refreshData}
                />
              ))}
            </div>
            <Button
              variant="outlined"
              onClick={() => {
                setShowAddQualificationDialog(
                  QualificationType.TEACHING_QUALIFICATION
                )
              }}
              size="sm"
            >
              Add Teaching Qualification
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-onSurface-200 bg-white p-3">
          <Typography variant="title2" className="mb-3">
            Professional Experience
          </Typography>

          <div className="flex flex-wrap items-center gap-2">
            <AutoSaveSelect
              endIcon={<span className="me-2 text-onSurface-500">years</span>}
              className="!w-auto"
              label="Experience as School Teacher (In Years)"
              placeholder="Ex - 4 years"
              saveKey="yearsOfExperience"
              userPrimaryKey={id || ""}
              value={formatSelectValue(
                yearsOfExperience,
                yearsOfExperienceChoices
              )}
              options={yearsOfExperienceChoices}
            />
            <AutoSaveSelect
              isMulti
              label="Board / Curriculum Taught"
              placeholder="Ex - CBSE"
              saveKey="curriculumsTaught"
              value={formatSelectValue(curriculumsTaught, curriculaChoices)}
              userPrimaryKey={id || ""}
              options={curriculaChoices}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default Step1
